import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';

const slugify = (value) => (value || 'torneo')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 40) || 'torneo';

const etiquetaFranja = (franja) => {
  if (!franja) return '';
  if (franja.horaDesde && franja.horaHasta) return `${franja.nombre} (${franja.horaDesde}-${franja.horaHasta})`;
  if (!franja.horaDesde && franja.horaHasta) return `${franja.nombre} (hasta ${franja.horaHasta})`;
  if (franja.horaDesde && !franja.horaHasta) return `${franja.nombre} (después ${franja.horaDesde})`;
  return franja.nombre;
};

const horarioDeGrupo = (grupo) => {
  const primera = grupo.parejas?.[0];
  if (primera?.diaSemana != null && primera?.franjaId != null) {
    return { diaSemana: primera.diaSemana, franjaId: primera.franjaId };
  }
  return {
    diaSemana: grupo.tgr_dia_semana ?? primera?.diaSemana ?? null,
    franjaId: grupo.tgr_franja_id ?? primera?.franjaId ?? null,
  };
};

const signed = (n) => {
  const v = Number(n) || 0;
  return v > 0 ? `+${v}` : String(v);
};

const textoHorarioGrupo = (grupo, dias, franjas) => {
  const horario = horarioDeGrupo(grupo);
  const dia = dias.find((d) => d.diaSemana === horario.diaSemana);
  const franja = franjas.find((f) => f.id === horario.franjaId);
  return [dia?.nombre, etiquetaFranja(franja)].filter(Boolean).join('  ·  ');
};

const formatearResultado = (partido) => {
  if (partido.tpg_estado !== 'JUGADO' && partido.tpg_estado !== 'WO') return 'Pendiente';
  const base = partido.resultado_formateado
    || `${partido.tpg_sets_a ?? 0} - ${partido.tpg_sets_b ?? 0}`;
  return partido.tpg_estado === 'WO' ? `${base} WO` : base;
};

const formatearRonda = (partido) => {
  if (partido.tpg_ronda === 2) return '2 (cruce)';
  return String(partido.tpg_ronda ?? 1);
};

const HEAD_COLOR = [24, 144, 255];
const HEAD_TEXT = [255, 255, 255];
const CLASIFICADO_FILL = [210, 242, 214];
const ALT_FILL = [248, 249, 250];

export const exportarFaseGruposPdf = ({
  torneo,
  grupos = [],
  dias = [],
  franjas = [],
  faseCompleta = false,
}) => {
  const gruposOrd = [...grupos].sort((a, b) => (a.tgr_orden ?? 0) - (b.tgr_orden ?? 0));
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const footerReserve = 14;

  const nombreTorneo = torneo?.tor_nombre || 'Torneo';
  const fechaTorneo = torneo?.tor_fecha ? dayjs(torneo.tor_fecha).format('DD/MM/YYYY') : null;
  const incluirStb = torneo?.tor_super_tie_break !== false && (torneo?.tor_cantidad_sets ?? 3) === 3;
  const clasificadosPorGrupo = torneo?.tor_clasificados_por_grupo ?? 2;

  const tableCommon = {
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: 1.6,
      overflow: 'linebreak',
      valign: 'middle',
      textColor: [33, 33, 33],
      lineColor: [220, 220, 220],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: HEAD_COLOR,
      textColor: HEAD_TEXT,
      fontStyle: 'bold',
      fontSize: 8,
      halign: 'center',
    },
    margin: { left: margin, right: margin, bottom: footerReserve },
  };

  const drawHeader = (isFirstPage) => {
    const y = margin;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(isFirstPage ? 16 : 11);
    doc.setTextColor(20, 20, 20);
    doc.text(nombreTorneo, margin, y + 5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(90, 90, 90);
    doc.text(isFirstPage ? 'Fase de grupos — Resultados' : 'Fase de grupos — Resultados (cont.)', margin, y + 11);

    const meta = [
      fechaTorneo ? `Fecha: ${fechaTorneo}` : null,
      faseCompleta ? 'Estado: completa' : 'Estado: en curso',
    ].filter(Boolean).join('   ·   ');
    doc.setFontSize(8.5);
    doc.text(meta, pageW - margin, y + 5, { align: 'right' });

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(margin, y + 14, pageW - margin, y + 14);
    return y + 18;
  };

  const ensureSpace = (currentY, needed) => {
    if (currentY + needed <= pageH - footerReserve) return currentY;
    doc.addPage();
    return drawHeader(false);
  };

  let y = drawHeader(true);

  gruposOrd.forEach((grupo, idx) => {
    y = ensureSpace(y, 42);

    const horarioTxt = textoHorarioGrupo(grupo, dias, franjas);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(20, 20, 20);
    doc.text(`Grupo ${grupo.tgr_nombre}`, margin, y + 4);

    if (horarioTxt) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(90, 90, 90);
      doc.text(horarioTxt, pageW - margin, y + 4, { align: 'right' });
    }

    y += 8;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(70, 70, 70);
    doc.text('Tabla de posiciones', margin, y);
    y += 2;

    const headTabla = ['#', 'Pareja', 'PJ', 'PG', 'PP', 'Pts', 'Sets +/-', 'Games +/-'];
    if (incluirStb) headTabla.push('STB +/-');

    const bodyTabla = (grupo.tabla || []).map((row, i) => {
      const line = [
        String(i + 1),
        row.etiqueta || '-',
        String(row.pj ?? 0),
        String(row.pg ?? 0),
        String(row.pp ?? 0),
        String(row.puntos ?? 0),
        signed((row.sets_favor ?? 0) - (row.sets_contra ?? 0)),
        signed((row.games_favor ?? 0) - (row.games_contra ?? 0)),
      ];
      if (incluirStb) {
        line.push(signed((row.stb_favor ?? 0) - (row.stb_contra ?? 0)));
      }
      return line;
    });

    autoTable(doc, {
      ...tableCommon,
      startY: y,
      head: [headTabla],
      body: bodyTabla.length ? bodyTabla : [['-', 'Sin parejas', '-', '-', '-', '-', '-', ...(incluirStb ? ['-'] : [])]],
      columnStyles: {
        0: { cellWidth: 8, halign: 'center', fontStyle: 'bold' },
        1: { cellWidth: 'auto', halign: 'left' },
        2: { cellWidth: 12, halign: 'center' },
        3: { cellWidth: 12, halign: 'center' },
        4: { cellWidth: 12, halign: 'center' },
        5: { cellWidth: 12, halign: 'center', fontStyle: 'bold' },
        6: { cellWidth: 18, halign: 'center' },
        7: { cellWidth: 20, halign: 'center' },
        8: { cellWidth: 18, halign: 'center' },
      },
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        if (data.row.index < clasificadosPorGrupo && bodyTabla.length) {
          data.cell.styles.fillColor = CLASIFICADO_FILL;
          data.cell.styles.fontStyle = data.column.index === 1 || data.column.index === 5
            ? 'bold'
            : data.cell.styles.fontStyle;
        } else if (data.row.index % 2 === 1) {
          data.cell.styles.fillColor = ALT_FILL;
        }
      },
    });

    y = (doc.lastAutoTable?.finalY ?? y) + 8;
    y = ensureSpace(y, 28);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(70, 70, 70);
    doc.text('Partidos', margin, y);
    y += 2;

    const partidos = grupo.partidos || [];
    autoTable(doc, {
      ...tableCommon,
      startY: y,
      head: [['Ronda', 'Pareja A', 'Pareja B', 'Resultado']],
      body: partidos.length
        ? partidos.map((p) => [
          formatearRonda(p),
          p.pareja_a || '-',
          p.pareja_b || '-',
          formatearResultado(p),
        ])
        : [['-', 'Sin partidos', '-', '-']],
      columnStyles: {
        0: { cellWidth: 22, halign: 'center' },
        1: { cellWidth: 'auto', halign: 'left' },
        2: { cellWidth: 'auto', halign: 'left' },
        3: { cellWidth: 42, halign: 'center' },
      },
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        if (data.column.index === 3 && data.cell.raw === 'Pendiente') {
          data.cell.styles.textColor = [160, 160, 160];
          data.cell.styles.fontStyle = 'italic';
        }
        if (data.row.index % 2 === 1) {
          data.cell.styles.fillColor = ALT_FILL;
        }
      },
    });

    y = (doc.lastAutoTable?.finalY ?? y) + (idx < gruposOrd.length - 1 ? 12 : 6);
  });

  const pageCount = doc.getNumberOfPages();
  const generado = dayjs().format('DD/MM/YYYY HH:mm');
  const leyenda = clasificadosPorGrupo === 1
    ? 'Fila verde: 1° clasificado'
    : `Filas verdes: 1° y 2° clasificados`;
  for (let p = 1; p <= pageCount; p += 1) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(`Generado ${generado}  ·  ${leyenda}`, margin, pageH - 8);
    doc.text(`Página ${p} de ${pageCount}`, pageW - margin, pageH - 8, { align: 'right' });
  }

  const fechaArchivo = dayjs().format('YYYY-MM-DD');
  doc.save(`resultados-grupos-${slugify(nombreTorneo)}-${fechaArchivo}.pdf`);
};
